import React from 'react';
import { usePlotArea, useXAxisInverseScale, useYAxisScale } from 'recharts';

// ChartCrosshair — continuous fine-grained hover readout for recharts
// line charts (live-user feedback 2026-07: the default tooltip snaps to
// the ~30–60 simulation points, so subtle features and exact crossover
// points could not be read).
//
// Instead of nearest-point snapping, the mouse x-pixel is inverted
// through the real x-axis scale (recharts v3 `useXAxisInverseScale`) and
// each visible series is LINEARLY INTERPOLATED at that exact voltage.
// The plotted data is untouched — only the readout is densified.
//
// Usage:
//   const [hoverX, setHoverX] = useState(null);
//   <div onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect();
//                            setHoverX(e.clientX - r.left); }}
//        onMouseLeave={() => setHoverX(null)}>
//     <ResponsiveContainer><LineChart ...>
//       ...
//       <ChartCrosshair hoverX={hoverX} series={[{ name, color, points: [{x,y}] }]} />
//     </LineChart></ResponsiveContainer>
//   </div>
//
// `points` must be sorted by x ascending.

// Linear interpolation of a sorted [{x, y}] series at x; null outside range.
export function interpY(pts, x) {
  if (!pts || !pts.length) return null;
  if (x < pts[0].x || x > pts[pts.length - 1].x) return null;
  let lo = 0, hi = pts.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid].x <= x) lo = mid; else hi = mid;
  }
  const a = pts[lo], b = pts[hi];
  if (a.x === b.x) return a.y;
  const t = (x - a.x) / (b.x - a.x);
  return a.y + t * (b.y - a.y);
}

// 4-significant-digit readout formatter.
const sig = (v) => {
  if (v == null || !isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a !== 0 && (a < 1e-3 || a >= 1e5)) return v.toExponential(2);
  return String(Number(v.toPrecision(4)));
};

export default function ChartCrosshair({
  hoverX, series = [],
  xLabel = 'V', xUnit = 'V', yUnit = '',
  bg = '#0d1117', border = '#2a3f55', color = '#8ba3bb',
}) {
  // Hooks must run unconditionally (before any early return).
  const plot = usePlotArea();
  const xInv = useXAxisInverseScale();
  const yScale = useYAxisScale();

  if (hoverX == null || !plot || !xInv || !yScale) return null;
  const { x: left, y: top, width, height } = plot;
  if (hoverX < left || hoverX > left + width) return null;

  const xVal = Number(xInv(hoverX));
  if (!Number.isFinite(xVal)) return null;

  const trunc = (s) => (s && s.length > 20 ? s.slice(0, 19) + '…' : s || '');
  const rows = [];
  for (const s of series) {
    const y = interpY(s.points, xVal);
    if (y != null && Number.isFinite(y)) {
      rows.push({ name: trunc(s.name), color: s.color, y });
    }
  }
  if (!rows.length) return null;

  const fs = 11, lineH = 15, padBox = 7, charW = 6.7;
  const header = `${xLabel} = ${sig(xVal)}${xUnit ? ' ' + xUnit : ''}`;
  const longest = Math.max(
    header.length,
    ...rows.map(r => `${r.name}: ${sig(r.y)}${yUnit ? ' ' + yUnit : ''}`.length),
  );
  const boxW = Math.max(110, padBox * 2 + charW * longest);
  const boxH = padBox * 2 + lineH * (rows.length + 1);
  const boxX = hoverX + 14 + boxW > left + width ? hoverX - 14 - boxW : hoverX + 14;
  const boxY = top + 6;
  const mono = 'IBM Plex Mono, monospace';

  return (
    <g pointerEvents="none">
      <line x1={hoverX} x2={hoverX} y1={top} y2={top + height}
        stroke={color} strokeDasharray="4 3" strokeOpacity={0.8} />
      {rows.map((r, i) => {
        const cy = Number(yScale(r.y));
        if (!Number.isFinite(cy) || cy < top || cy > top + height) return null;
        return (
          <circle key={`dot-${i}`} cx={hoverX} cy={cy} r={3.5}
            fill={r.color} stroke={bg} strokeWidth={1} />
        );
      })}
      <rect x={boxX} y={boxY} width={boxW} height={boxH} rx={4}
        fill={bg} fillOpacity={0.93} stroke={border} />
      <text x={boxX + padBox} y={boxY + padBox + fs} fill={color}
        fontSize={fs} fontFamily={mono}>{header}</text>
      {rows.map((r, i) => (
        <text key={`txt-${i}`} x={boxX + padBox} y={boxY + padBox + fs + lineH * (i + 1)}
          fill={r.color} fontSize={fs} fontFamily={mono}>
          {r.name}: {sig(r.y)}{yUnit ? ' ' + yUnit : ''}
        </text>
      ))}
    </g>
  );
}
